import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  IonButton,
  IonPage,
  IonContent,
  IonHeader,
  IonLabel,
  IonList,
  IonIcon,
  IonGrid,
  IonCol,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonPopover,
  IonSpinner,
} from '@ionic/react';
import { add, remove, funnel} from 'ionicons/icons';
import {
  getAllProperties,
  getBookings,
  getBookingsStats
} from '../utilities/Storage';
import {storeBookings, storePropertiesFromBookings} from '../utilities/ApiTasks';
import { PaginatedFooter } from './JobsSelfEmployed/PaginatedFooter';
import { PushNotificationRedirector } from '../components/PushNotificationRedirector';
import { ClearFilter } from './JobsSelfEmployed/ClearFilter';
import { PropertyFilter } from './JobsSelfEmployed/PropertyFilter';
import { useHistory } from 'react-router-dom';
import { ApiContext } from '../contexts/ApiContext';
import {Toolbar} from "./Toolbar";
import {BookingRow} from "./BookingRow";
import {AuthContext} from "../contexts/AuthContext";
import {userIsCleaner} from "../dataStructures/user/predicates";

export default function Bookings() {
  const { dataInitialised } = useContext(ApiContext);
  const history = useHistory();

  const [items, setItems] = useState([]);
  const [allProperties, setAllProperties] = useState({});
  const [periodFilter, setPeriodFilter] = useState('30');
  const [propertySearchTerm, setPropertySearchTerm] = useState('');
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [pageLimit] = useState(20);
  const [itemsCount, setItemsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isPaginatingRef = useRef(false);
  const [popoverState, setShowPopover] = useState({
    showPopover: false,
    event: undefined,
  });

  const { user } = useContext(AuthContext);
  const isCleaner = userIsCleaner(user);

  React.useEffect(() => {
    if (!isCleaner) {
      history.push('/jobs');
    }
  }, [history]);

  const createQueryStringFilterValues = useCallback((selectedProperty=propertySearchTerm, selectedStatus=periodFilter) => ({
    status: selectedStatus,
    propertyId: selectedProperty,
  }), [periodFilter, propertySearchTerm]);

  const refresh = useCallback(async (
    pageNumber = currentPageNumber,
    refreshByPull = false,
    selectedProperty = propertySearchTerm,
    selectedPeriod = periodFilter
  ) => {
    if (isPaginatingRef.current) return;
    isPaginatingRef.current = true;

    if(!refreshByPull) {
      setIsLoading(true);
    }

    setCurrentPageNumber(pageNumber);
    setPropertySearchTerm(selectedProperty);
    setPeriodFilter(selectedPeriod);

    await storeBookings(
      pageNumber,
      pageLimit,
      createQueryStringFilterValues(selectedProperty, selectedPeriod),
    );

    const items = await getBookings();
    setItems(items);

    const stats = await getBookingsStats();
    setItemsCount(stats?.total?.value);
    setCurrentPageNumber(stats?.currentPage?.value);

    if(!refreshByPull) {
      setIsLoading(false);
    }
    isPaginatingRef.current = false;
  }, [
    currentPageNumber,
    propertySearchTerm,
    periodFilter,
    pageLimit,
    createQueryStringFilterValues,
  ]);

  useEffect(() => {
    let mounted = true;

    setIsLoading(true);

    if (!dataInitialised) return;

    const getData = async () => {
      let bookingsStats = await getBookingsStats();
      if (!bookingsStats?.currentPage?.length) {
        bookingsStats = {
          currentPage: {
            value: 1
          },
          pageLimit: {
            value: 20
          },
          filters: {
            status: 30
          }
        }
      }
      if(!await storeBookings(bookingsStats.currentPage.value, bookingsStats.pageLimit.value, bookingsStats.filters)) { return; }

      const stats = await getBookingsStats();
      const items = await getBookings();
      

      if (!mounted) return;
      setItemsCount(stats?.total?.value);
      setCurrentPageNumber(stats?.currentPage?.value);
      setItems(items);

      setIsLoading(false);
      
      //now carry on getting the properties in the "background"
      if (!await storePropertiesFromBookings(user.id)) { return; }
      const properties = await getAllProperties();
      if (Object.keys(allProperties).length === 0) {
        setAllProperties(properties);
      }
    };

    if (dataInitialised) {
      getData();
    };

    return () => {
      mounted = false;
    };
  }, [dataInitialised]);


  let numberOfFiltersApplied = 0;
  if (propertySearchTerm > 0) {
    numberOfFiltersApplied++;
  }

  const hasFiltersApplied = numberOfFiltersApplied > 0;
  const hasNoItems = Object.values(items).length === 0;
  let rowIsGrey = false;

  return (
    <IonPage>
      <IonHeader>
        <Toolbar title='Bookings' backButtonHref='/' />
      </IonHeader>

      <IonContent padding>
        <IonRefresher slot='fixed' pullMin={120} pullMax={180} onIonRefresh={event => {
          refresh(1, true).then(() => event.detail.complete());
        }}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <IonGrid>
          <IonRow class='ion-align-items-center'>
            <IonCol size='10'>
              <IonSelect
                key='BookingsDD'
                label='Period'
                justify='start'
                fill='solid'
                interface='popover'
                toggleIcon={add}
                expandedIcon={remove}
                value={periodFilter}
                onIonChange={(e) => {
                  refresh(1, false, propertySearchTerm, e.detail.value);
                }}
                aria-label='Status'
                class='full-width-select'
              >
                <IonSelectOption value='30'>
                  Current bookings
                </IonSelectOption>

                <IonSelectOption value='90'>
                  Past 90 days
                </IonSelectOption>

                <IonSelectOption value='180'>
                  Past 180 days
                </IonSelectOption>

                <IonSelectOption value='365'>
                  Past 365 days
                </IonSelectOption>
              </IonSelect>
            </IonCol>

            <IonCol size='2'>
              <IonPopover
                event={popoverState.event}
                isOpen={popoverState.showPopover}
                onDidDismiss={() => setShowPopover({ showPopover: false, event: undefined })}
              >
                <IonGrid fixed={true}>
                  <IonRow class='ion-align-items-center'>
                    <IonCol size='12'>
                      <PropertyFilter
                        properties={allProperties}
                        value={propertySearchTerm}
                        onChange={(propertySearchTerm) => {
                          refresh(1, false, propertySearchTerm, periodFilter);
                        }}
                      />
                    </IonCol>
                  </IonRow>

                  <IonRow>
                    <IonCol className='ion-text-right'>
                      <ClearFilter
                        onClick={() => {
                          refresh(1, false, '', periodFilter);
                          setShowPopover({ showPopover: false, event: undefined });
                        }}/>
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonPopover>

              <IonButton
                color='fern'
                size='medium'
                expand='block'
                onClick={(e) =>
                  setShowPopover({ showPopover: true, event: e.nativeEvent })
                }
              >
                <IonIcon icon={funnel} size='small' />
                {hasFiltersApplied && <IonLabel> <small>{numberOfFiltersApplied}</small></IonLabel>}
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>

        {isLoading ? (
          <div className='spinner-container'>
            <IonSpinner color='teal' />
          </div>
        ) : (

        <IonList lines='full'>
          {hasNoItems ? (
                <h3 key='noItems' className='ion-text-center'>No bookings found.</h3>
              ) : (
              Object.values(items).map((item) =>{
                  rowIsGrey = !rowIsGrey;
                  const color = rowIsGrey ? 'lgrey' : 'white';

                  return (
                      <BookingRow
                          key={item.value.id}
                          color={color}
                          item={item.value}
                          history={history}
                      />
                  );
                })
            )
          }
        </IonList>
      )}
      </IonContent>
      <PaginatedFooter
        currentPageNumber={currentPageNumber}
        itemsCount={itemsCount}
        pageLimit={pageLimit}
        onPageChange={(page) => {
          refresh(page, false);
        }}
        isLoading={isLoading}
      />

      <PushNotificationRedirector />
    </IonPage>
  );
}
